import { useCallback, useRef, useState } from 'react';
import { logError } from '../utils/logger';
import { BOOKING_URL } from '../constants/site';

const API = process.env.REACT_APP_BACKEND_URL;
const GREETING = "Hi! I'm Jess's assistant 👋 Ask me anything about buying or selling on the coast of VA & NC — or how to get a free home valuation.";
const FALLBACK = 'Sorry, I had trouble responding. Please call or text Jess at 252-564-5612.';
const STATUS_POLLS = 12;
const STATUS_INTERVAL_MS = 2000;

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `chat-${Date.now()}-${Math.random()}`);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const replaceLast = (list, content) => {
  const copy = [...list];
  copy[copy.length - 1] = { ...copy[copy.length - 1], content };
  return copy;
};

const readStream = async (body, onChunk) => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    onChunk(decoder.decode(value, { stream: true }));
  }
};

// Backend creates the lead a moment after the stream ends; poll briefly for it.
const waitForLead = async (sessionId) => {
  for (let i = 0; i < STATUS_POLLS; i += 1) {
    await sleep(STATUS_INTERVAL_MS);
    const res = await fetch(`${API}/api/chat/status?session_id=${encodeURIComponent(sessionId)}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.lead_captured) return data;
  }
  return null;
};

const bookingMessage = (lead) => ({
  id: newId(),
  role: 'assistant',
  kind: 'booking',
  content: `${lead.name ? `Thanks, ${lead.name}!` : 'Thanks!'} Jess has your details. Lock in a time that works for you on her calendar:`,
  bookingUrl: lead.booking_url || BOOKING_URL,
});

// Fire-and-forget: tells the backend to alert Jess that this lead opened her calendar.
export const notifyBookingOpened = (sessionId) =>
  fetch(`${API}/api/chat/booking-opened`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    keepalive: true,
  }).catch((err) => logError('Booking-opened notify failed:', err));

// Owns the chat transcript + streaming state for one visitor session.
export const useChatStream = () => {
  const [messages, setMessages] = useState([{ id: 'greeting', role: 'assistant', content: GREETING }]);
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(newId());
  const sharedEmail = useRef(false);
  const leadCaptured = useRef(false);

  const checkLead = useCallback(async () => {
    if (leadCaptured.current || !sharedEmail.current) return;
    try {
      const lead = await waitForLead(sessionId.current);
      if (lead && !leadCaptured.current) {
        leadCaptured.current = true;
        setMessages((m) => [...m, bookingMessage(lead)]);
      }
    } catch (err) {
      logError('Chat lead status failed:', err);
    }
  }, []);

  const send = useCallback(async (text) => {
    if (text.includes('@')) sharedEmail.current = true;
    setMessages((m) => [...m, { id: newId(), role: 'user', content: text }, { id: newId(), role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error(`chat error ${res.status}`);
      await readStream(res.body, (chunk) => setMessages((m) => replaceLast(m, m[m.length - 1].content + chunk)));
    } catch (err) {
      logError('Chat stream failed:', err);
      setMessages((m) => replaceLast(m, FALLBACK));
    }
    setStreaming(false);
    checkLead();
  }, [checkLead]);

  return { messages, streaming, send, sessionId: sessionId.current };
};
